import { Map as ImmutableMap } from 'immutable';
// CORE
import * as React from 'react';
import { useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import Box from '@mui/material/Box';
// MUI
import DialogContent from '@mui/material/DialogContent';
import { goalsActions, goalsSelectors, goalsTypes } from 'app-components/flux/goals';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import { tracker } from 'app-components/utils/core';
import { makeStyles } from 'tss-react/mui';

import {
  DIALOG_TYPE as CONFIRMATION_DIALOG_TYPE,
  mkConfirmationDialogProps,
} from 'components/Dialogs/ConfirmationDialog';
// SHARED
import StdDialog from 'components/Dialogs/StdDialog';
import LoadingView from 'components/LoadingView';

// LOCAL
import { EPOCH_ISO } from 'app-components/utils/date';
import GoalAmounts from './GoalAmounts';
import GoalDone from './GoalDone';
import GoalFinalize from './GoalFinalize';
import GoalPicker from './GoalPicker';
import GoalTargets from './GoalTargets';
import { AddGoalContext, goalTypeToMixpanelType, initialAddGoalState, reduceAddGoal } from './state';
import type { specificGoalType } from './state';

const useStyles = makeStyles()((theme, tabIndex: any) => ({
  contentContainer: {
    overflowY: tabIndex === 1 ? 'hidden' : 'auto',
    padding: tabIndex > 0 ? 0 : theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
  },
}));

/*  Components in order of tabIndex are as follows
 * 1. GoalPicker - selecting the type of goal
 * 2. GoalAmounts - setting targetAmount and savedSoFar
 * 3. GoalTargets - set targetDate, startDate, and contributionAmount
 * 4. GoalFinalize - set include in spending plan or not
 * 5. GoalDone - just the last page that says "high five, you created a goal"
 * */

/* The steps taken after a user presses the submit button to create a goal are as follows
 *     - They are all inside this file and can be found by comments like => "Step [#]."
 * Step 1. create tag associated with goal
 * Step 2. once the new tag has been created we extract the real id for it and attach it to the goal
 * Step 3. we submit the goal for creation
 * --- the following are only if the goal has a savedSoFar value > 0 --- *
 * Step 4. once the goal has been created and accounts have been updated we extract the accountId for the goal
 *             submit the transfer transaction to establish the savedSoFar value
 * */

// This serves as the parent that manages the different pages required for adding a goal
//  WARNING: This and all other files in the AddGoal directory reference the same Context
//            meaning they only work together and not individually
function AddGoal(props: {
  context: string;
  name?: string;
  type?: specificGoalType;
}) {
  const { context, name, type } = props;
  const dispatch = useDispatch();

  // State values
  const [contextState, contextDispatch] = useReducer(reduceAddGoal, initialAddGoalState);
  const { tabIndex, madeChanges } = contextState;
  const { classes } = useStyles(tabIndex, {
    props: tabIndex,
  });

  useEffect(() => {
    if (type && name) {
      contextDispatch({ type: 'update', payload: { type, tabIndex: 1, name } });
    }
  }, [name, type]);

  const goalsById: any = useSelector(goalsSelectors.getResourcesById);

  const [waitingOnGoalWithClientId, setWaitingOnGoalWithClientId] = useState<string | null>(null);
  const [errorState, setErrorState] = useState(false);

  // Callback functions
  const handleClose = useCallback(() => {
    contextDispatch({ type: 'reset' });
    dispatch(rootUiActions.removeDialog());
  }, [dispatch, contextDispatch]);

  const handleTryToClose = useCallback(() => {
    if (tabIndex === 4 || waitingOnGoalWithClientId) {
      return;
    }
    if (!madeChanges) {
      handleClose();
      return;
    }

    dispatch(
      rootUiActions.createDialog(
        rootUiTypes.mkRootUiData({
          id: 'close-add-goal',
          type: CONFIRMATION_DIALOG_TYPE,
          allowNesting: true,
          props: ImmutableMap(
            mkConfirmationDialogProps({
              title: 'Discard changes',
              content: 'Are you sure you want to discard all the changes?',
              confirmLabel: 'Discard',
              denyLabel: 'Go Back',
              confirmAction: () => {
                dispatch(rootUiActions.removeDialog());
                handleClose();
              },
            }),
          ),
        }),
      ),
    );
  }, [handleClose, dispatch, madeChanges, tabIndex, waitingOnGoalWithClientId]);

  const handleBack = useCallback(() => {
    contextDispatch({ type: 'update', payload: { tabIndex: tabIndex - 1 } });
  }, [tabIndex, contextDispatch]);

  const handleSubmit = useCallback(() => {
    const {
      name,
      type,
      targetAmount,
      targetDate,
      contributionAmount,
      startDate,
      savedSoFar,
      includeInF2S,
      ignoredSuggestions,
      contributions,
    } = contextState;
    const goalClientId = uuidv4().toUpperCase();
    setWaitingOnGoalWithClientId(goalClientId);
    const goal = goalsTypes.mkGoal(type.type, {
      name,
      targetAmount,
      contributionAmount,
      savedSoFar,
      targetOn: targetDate?.toISODate(),
      startOn: startDate?.toISODate(),
      completedOn: savedSoFar >= targetAmount ? startDate?.toISODate() : EPOCH_ISO,
      clientId: goalClientId,
      isTakenFromF2S: includeInF2S,
    });

    new Promise((resolve, reject) => {
      const filteredContributions = contributions.filter(
        (x: null | { accountId?: string; amount?: string }) => x?.amount && x?.accountId,
      );
      dispatch(
        goalsActions.performCreateGoal(
          { goal, contributions: filteredContributions },
          { resolve, reject, undo: { userMessage: 'Goal created', disableUndoAction: true }, context },
        ),
      );
    })
      .then(() => {
        setWaitingOnGoalWithClientId(null);
        contextDispatch({ type: 'update', payload: { tabIndex: 4 } });
      })
      .catch(() => {
        setWaitingOnGoalWithClientId(null);
        setErrorState(true);
        contextDispatch({ type: 'update', payload: { tabIndex: 3 } });
      });

    // Mixpanel tracking
    tracker.track(tracker.events.createGoal, {
      // @ts-expect-error FIXME TS7053
      type: goalTypeToMixpanelType[type.type] || 'custom',
      savedSoFar: savedSoFar && savedSoFar > 0,
      date: Boolean(targetDate),
      savedInError: ignoredSuggestions,
    });
    if (goalsById.size === 0) {
      tracker.track(tracker.events.checklistItemComplete, { section: 'goals' });
    }
    tracker.track(tracker.events.editSpendingPlanBucket, { location: 'goals', field: 'add' });
  }, [dispatch, contextDispatch, contextState, goalsById, context]);

  // Memoized values
  const pageContent = useMemo(() => {
    if (waitingOnGoalWithClientId) {
      return (
        <Box height={'400px'}>
          <LoadingView />
        </Box>
      );
    }
    switch (tabIndex) {
      case 4:
        return <GoalDone />;
      case 3:
        return <GoalFinalize error={errorState} />;
      case 2:
        setErrorState(false);
        return <GoalTargets />;
      case 1:
        return <GoalAmounts />;
      case 0:
      default:
        return <GoalPicker />;
    }
  }, [errorState, tabIndex, waitingOnGoalWithClientId]);

  const stateContextValue = useMemo(
    () => ({
      state: contextState,
      dispatch: contextDispatch,
      onSubmit: handleSubmit,
    }),
    [contextState, contextDispatch, handleSubmit],
  );

  return (
    <AddGoalContext.Provider value={stateContextValue}>
      <StdDialog
        onBack={handleBack}
        onClose={handleTryToClose}
        open
        sharedcomponentid='ADD_GOAL_DIALOG'
        showBackButton={tabIndex > 0 && tabIndex !== 4}
        showCloseButton
        size='md'
        title='Create a goal'
      >
        <DialogContent className={classes.contentContainer}>{pageContent}</DialogContent>
      </StdDialog>
    </AddGoalContext.Provider>
  );
}

export default AddGoal;
